import type { UIDataTypes, UIMessage, UITools } from 'ai'
import { z } from 'zod'
import type { AvailableTool } from './index'

export const chatMessageMetadataSchema = z
    .object({
        provider: z
            .enum([
                'openai',
                'anthropic',
                'google',
                'xiaomi',
                'moonshotai',
                'deepseek',
                'zai',
                'alibaba',
                'xai',
            ])
            .optional(),
        model: z.string().optional(),
        effort: z.enum(['quick', 'deep']).optional(),
        finishReason: z.string().optional(),
        promptTokens: z.number().optional(),
        completionTokens: z.number().optional(),
        totalTokens: z.number().optional(),
        createdAt: z.string().optional(),
        /** Set when the turn ended without a `finish` chunk. 'abort' means the
         * SDK saw an abort chunk (stop button); 'cancel' means the UI stream
         * was torn down from downstream without one (what a client disconnect
         * looks like server-side). Absent on turns that finished normally. */
        interruption: z.enum(['abort', 'cancel']).optional(),
        /** Whether the HTTP request's abort signal had fired by the time the
         * interrupted turn was persisted — distinguishes a client disconnect
         * from a cancellation with an unknown cause. */
        /** Legacy name for stopRequested, on turns interrupted before this field existed. */
        clientDisconnected: z.boolean().optional(),
        stopRequested: z.boolean().optional(),
        /** On a user message sent by a scheduled agent rather than typed by the user. */
        scheduledAgentRunId: z.number().optional(),
    })
    .passthrough()

export const entitySetDataSchema = z.object({
    attachmentId: z.string(),
    entityType: z.enum(['campaign', 'adgroup', 'keyword']),
    name: z.string(),
    entities: z.array(z.any()),
    dateRange: z
        .object({
            from: z.string(),
            to: z.string(),
        })
        .optional(),
})

export const entityMentionDataSchema = z.object({
    attachmentId: z.string(),
    entityType: z.enum(['campaign', 'adgroup', 'keyword']),
    id: z.string(),
    name: z.string(),
    parentId: z.string().optional(),
    parentName: z.string().optional(),
    resourceName: z.string(),
})

export const contextDataSchema = z.object({
    attachmentId: z.string(),
    kind: z.enum(['text', 'file', 'image']),
    name: z.string(),
    mediaType: z.string().optional(),
    size: z.number().optional(),
    text: z.string().optional(),
    url: z.string().optional(),
    blobId: z.string().optional(),
    sandboxPath: z.string().optional(),
    previewUrl: z.string().optional(),
})

export const compactionSummaryDataSchema = z.object({
    summary: z.string(),
    tokensCompacted: z.number(),
    model: z.string(),
    createdAt: z.string(),
})

/** Server-added: system-prompt lines that changed since the model last saw them. */
export const promptUpdateDataSchema = z.object({
    text: z.string(),
})

export const chatDataPartSchemas = {
    'entity-set': entitySetDataSchema,
    'entity-mention': entityMentionDataSchema,
    context: contextDataSchema,
    'compaction-summary': compactionSummaryDataSchema,
    'prompt-update': promptUpdateDataSchema,
} satisfies Record<string, z.ZodTypeAny>

export type ChatMessageMetadata = z.infer<typeof chatMessageMetadataSchema>

export interface ChatSdkDataParts extends UIDataTypes {
    'entity-set': z.infer<typeof entitySetDataSchema>
    'entity-mention': z.infer<typeof entityMentionDataSchema>
    context: z.infer<typeof contextDataSchema>
    'compaction-summary': z.infer<typeof compactionSummaryDataSchema>
    'prompt-update': z.infer<typeof promptUpdateDataSchema>
}

export type ChatSdkMessage<TOOLS extends UITools = UITools> = UIMessage<
    ChatMessageMetadata,
    ChatSdkDataParts,
    TOOLS
>

export interface ChatThreadSummary {
    id: string
    title: string | null
    createdAt: string
    updatedAt: string | null
    accountId: string
}

export interface ChatThreadRecord extends ChatThreadSummary {
    source: string
    contextPrompt: string | null
    /** Sticky-assigned model for this thread. Null on threads created before
     * the sticky-assignment migration; populated on first send. */
    provider: string | null
    model: string | null
    messages: ChatSdkMessage[]
}

export interface ChatSharedThreadResponse {
    profileImageUrl: string | null
    id: string
    createdAt: string
    updatedAt: string | null
    deletedAt: string | null
    accountId: string
    userId: number
    title: string
    messages: ChatSdkMessage[]
    accountName: string
    accountColour: string
    platform: number
    availableTools: AvailableTool[]
}

export function isContextDataPart(
    part: ChatSdkMessage['parts'][number]
): part is Extract<ChatSdkMessage['parts'][number], { type: 'data-context' }> {
    return part.type === 'data-context'
}

export function isEntitySetDataPart(
    part: ChatSdkMessage['parts'][number]
): part is Extract<ChatSdkMessage['parts'][number], { type: 'data-entity-set' }> {
    return part.type === 'data-entity-set'
}

export function isEntityMentionDataPart(
    part: ChatSdkMessage['parts'][number]
): part is Extract<ChatSdkMessage['parts'][number], { type: 'data-entity-mention' }> {
    return part.type === 'data-entity-mention'
}

export function isCompactionSummaryDataPart(
    part: ChatSdkMessage['parts'][number]
): part is Extract<ChatSdkMessage['parts'][number], { type: 'data-compaction-summary' }> {
    return part.type === 'data-compaction-summary'
}
